import { useEffect, useRef, useState } from "react";
import { AppBar, Box, Container, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popper, Tab, Tabs, Toolbar, Typography, useMediaQuery } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import Logo from '../../images/logo_coloured.avif'
import { Link, matchPath, useLocation, useNavigate } from "react-router";
import { useColorScheme } from '@mui/material/styles';
import theme from "../../constants/theme";
import StyledSvgIcon from "../../components/reusable/StyledSvgIcon";
import { ReactComponent as MenuOpenSpoke } from '../../SVG/menu_open_spoke.svg'
import { ReactComponent as Lemon } from '../../SVG/lemon.svg'
import { ReactComponent as LemonColored } from '../../SVG/lemon_2.svg'
import { ReactComponent as Menu } from '../../SVG/menu_food.svg'
import { ReactComponent as About } from '../../SVG/article_person.svg'
import { ReactComponent as Locations } from '../../SVG/table_restaurant.svg'
import { ReactComponent as Order } from '../../SVG/takeout.svg'
import { ReactComponent as Cone } from '../../SVG/cone.svg'
import StyledButton from "../../components/reusable/StyledButton";
import PeopleAltIcon from '@mui/icons-material/PeopleAltOutlined';
import PinDropIcon from '@mui/icons-material/PinDropOutlined';

const useStyles = makeStyles()((theme) => ({
  header: {
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  content: {
    display: 'flex',
    padding: '16px',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.background.default,
    color: theme.palette.text.primary
  },
  logo: {
    height: '50px',
    minWidth: '150px',
    cursor: 'pointer',
    overflow: 'auto'
  },
  tabs: {
    '& .MuiTabs-scrollButtons': {
  opacity: 1,
  color: 'black'
    }
  },
  tab: {
      height: '40px',
      minHeight: '40px',
      color: theme.palette.text.primary,
      fontWeight: 600,
  },
  pulseButton: {
    minWidth: '100px',
    animation: 'pulse 2s infinite',
    marginLeft: '12px',
    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
        boxShadow: `0 0 0 0 ${theme.palette.primary.main}`,
      },
      '70%': {
        transform: 'scale(1.05)',
        boxShadow: '0 0 0 12px rgba(25,118,210,0)',
      },
      '100%': {
        transform: 'scale(1)',
        boxShadow: '0 0 0 0 rgba(25,118,210,0)',
      },
    },
  },
  popper: {
    position: "relative",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
    borderRadius: '12px',
    boxShadow: theme.shadows[8],
    padding: '12px 16px 12px 16px',
    maxWidth: 240,
    cursor: 'pointer',

    "&::before": {
      content: '""',
      position: "absolute",
      width: 20,
      height: 20,
      backgroundColor: theme.palette.primary.main,
      top: -10,
      left: 80,
      transform: "rotate(45deg)",
      borderTopLeftRadius: 4,
      zIndex: -4,
      boxShadow: theme.shadows[8],
    }
  }
}))

export default function AppHeader() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const mediaQuery = useMediaQuery(theme.breakpoints.down('md'));
  const headerRef = useRef(null)
  const [popperTarget, setPopperTarget] = useState<Element | null>(null)
  const { mode, setMode } = useColorScheme();
  const [checked, setChecked] = useState(mode === 'dark' ? true : false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  // const { auth } = useAuth();

  useEffect(() => {
    mode !== undefined
      &&
    setChecked(mode === 'dark' ? true : false)
  }, [mode])

  useEffect(() => {
    if (headerRef.current) {
      setPopperTarget(headerRef.current)
    }
  }, [])
  
  const handleModeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.checked === true ? 'dark' : 'light' )
    setChecked(event.target.checked)
  }

  function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

  function NavTabs() {
    const routeMatch = useRouteMatch(['/menu', '/about', '/order', '/locations', '/catering', '/login']);
    const currentTab = routeMatch?.pattern?.path;

    return (
      <Tabs 
        className={classes.tabs}
        value={currentTab || false}
        variant="scrollable"
        allowScrollButtonsMobile
        scrollButtons={true}
        indicatorColor="secondary"
      >
        <Tab
          className={classes.tab}
          icon={
            <StyledSvgIcon 
              component={Menu} 
              viewBox="0 -960 960 960"
              sx={{ fontSize: '24px', height: '24px' }}
            />
          }
          iconPosition="start"
          label="Menu" 
          value="/menu" 
          to="/menu" 
          component={Link} 
        />
        <Tab
          className={classes.tab}
          icon={
            <StyledSvgIcon 
              component={Order} 
              viewBox="0 -960 960 960"
              sx={{ fontSize: '24px', height: '24px' }}
            />
          }
          iconPosition="start"
          label="Order Online" 
          value="/order" 
          to="/order" 
          component={Link} 
        />
        <Tab
          className={classes.tab}
          icon={
            <StyledSvgIcon 
              component={Cone} 
              viewBox="0 0 1300 1300"
              sx={{ fontSize: '24px', height: '24px', transform: 'rotate(-45deg)' }}
            />
          }
          iconPosition="start"
          label="Catering" 
          value="/catering" 
          to="/catering" 
          component={Link} 
        />
        <Tab
          className={classes.tab}
          icon={
            <PinDropIcon/>
          }
          iconPosition="start"
          label="Locations" 
          value="/locations" 
          to="/locations" 
          component={Link} 
        />
        <Tab
          className={classes.tab}
          icon={
            <StyledSvgIcon 
              component={About} 
              viewBox="0 -960 960 960"
              sx={{ fontSize: '24px', height: '24px' }}
            />
          }
          iconPosition="start"
          label="About us" 
          value="/about" 
          to="/about" 
          component={Link} 
        />
        <Tab
          className={classes.tab}
          icon={
            <PeopleAltIcon />
          }
          iconPosition="start"
          label="Team Login" 
          value="/login" 
          to="/login"
          target="_blank"
          component={Link} 
        />
      </Tabs>
    );
  }

  return (
    <>
      <AppBar position="sticky" sx={{ background: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`}} ref={headerRef} id='main_header'>
        <Container maxWidth={false}>
          <Toolbar disableGutters >

            {/* Logo */}
            <img className={classes.logo} src={Logo} onClick={() => navigate('/')}/>
            <Box sx={{display: 'flex', flexGrow: '1', overflow: 'auto', padding: '8px', minHeight: '48px'}}>

              {/* Order Now button */}
              <StyledButton
                variant="contained"
                size="small"
                href="/order"
                fullWidth={false}
                className={classes.pulseButton}
              >
                <Typography variant="button" sx={{fontWeight: 600}}>Order</Typography>
              </StyledButton>

              {/* Nav Tabs; vw >= tablet */}
              {
                !mediaQuery
                  &&
                <NavTabs/>
              }

            </Box>

            {/* Hamburg menu; vw <= tablet */}
            {
              mediaQuery
                &&
              <IconButton
                onClick={() => setDrawerOpen(true)}
              >
                <StyledSvgIcon component={MenuOpenSpoke} viewBox="0 0 1042 1650" style={{fontSize: '32px', color: theme.palette.primary.main, transform: 'rotate(180deg)'}}/>
                {/* <StyledSvgIcon component={LemonOpen} viewBox='0 0 640 640' style={{fontSize: '32px', color: 'primary.main'}}/> */}
              </IconButton>
            }

          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        anchor="right"
      >
        <List sx={{padding:'initial'}}>

          {/* Drawer navigation options */}
          <ListItem key={'home'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {

                drawerOpen && navigate('/')
                setDrawerOpen(false)
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {
                  location.pathname === '/'
                    ?
                  <StyledSvgIcon component={LemonColored} viewBox="0 0 640 640" sx={{fontSize: '48px'}}/>
                    :
                  <StyledSvgIcon component={Lemon} viewBox="0 0 348 640" sx={{color: 'text.primary', fontSize: '48px'}}/>
                }
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/' ? 'primary.main' : 'text.primary',
                  fontWeight: 600,
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  Home
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>

          <ListItem key={'menu'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {

                drawerOpen && navigate('/menu')
                setDrawerOpen(false)
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <StyledSvgIcon component={Menu} viewBox="0 -960 960 960" sx={{color: location.pathname === '/menu' ? theme.palette.secondary.main : 'text.primary', fontSize: '48px'}}/>
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/menu' ? 'primary.main' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  Menu
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>

          <ListItem key={'order'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {

                drawerOpen && navigate('/order')
                setDrawerOpen(false)
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <StyledSvgIcon component={Order} viewBox="0 -960 960 960" sx={{color: location.pathname === '/order' ? theme.palette.secondary.main : 'text.primary', fontSize: '48px'}}/>
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/order' ? 'primary.main' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  Order Online
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>

          <ListItem key={'catering'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {

                drawerOpen && navigate('/catering')
                setDrawerOpen(false)
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <StyledSvgIcon component={Cone} viewBox="0 0 1300 1300" sx={{color: location.pathname === '/catering' ? theme.palette.secondary.main : 'text.primary', fontSize: '48px', transform: 'rotate(-45deg)'}}/>
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/catering' ? 'primary.main' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  Catering
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>

          <ListItem key={'locations'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {

                drawerOpen && navigate('/locations')
                setDrawerOpen(false)
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PinDropIcon sx={{color: location.pathname === '/locations' ? theme.palette.secondary.main : 'text.primary', fontSize: '48px'}}/>
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/locations' ? 'primary.main' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  Locations
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>

          <ListItem key={'about'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {

                drawerOpen && navigate('/about')
                setDrawerOpen(false)
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <StyledSvgIcon component={About} viewBox="0 -960 960 960" sx={{color: location.pathname === '/about' ? theme.palette.secondary.main : 'text.primary', fontSize: '48px'}}/>
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/about' ? 'primary.main' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  About Us
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>

          <ListItem key={'login'} disablePadding >
            <ListItemButton 
              sx={{
                minHeight: 48,
                px: '8px',
              }}
              onClick={() => {
                setDrawerOpen(false)
              }}
              href="/login"
              target="_blank"
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PeopleAltIcon sx={{color: location.pathname === '/login' ? theme.palette.secondary.main : 'text.primary', fontSize: '48px'}}/>
              </ListItemIcon>
              <ListItemText 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0, 
                  color: location.pathname === '/login' ? 'primary.main' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{fontWeight: 600, textTransform: 'uppercase'}}>
                  Team Login
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      
      {/* <Popper
        open={Boolean(headerRef.current)}
        anchorEl={headerRef.current}
        sx={{zIndex: 4}}
        placement="bottom-start"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [10, 14],
            },
          },
        ]}
      >
        <Box
          className={classes.popper}
          onClick={() => window.open('https://order.toasttab.com/egiftcards/gyrosa-102-1517-admirals-road', '_blank')}
        >
          <Typography variant='h4' sx={{ color: 'white', fontSize: '14px', textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)', textTransform: 'uppercase'}}>Gift Cards Available!</Typography>
        </Box>
      </Popper> */}
    </>
  )
}